/**
 * One plain-language sentence for what Penelope is doing on a game — the basic
 * tier's whole readout. Pure: the ticket and the strip hand it the game row's
 * facts and print what comes back. Regimes are the relay's
 * (relay/src/services/penelope/decide.ts); the words are the trader's.
 */
import type { PenelopeRegime } from '@/hooks/usePenelope';

export interface PenelopeStatusInput {
  /** cfg.enabled */
  armed: boolean;
  /** Armed but not running, and why (row.parked). */
  parked: string | null;
  /** Shadow mode: nothing reaches Kalshi. */
  practice: boolean;
  teamA: string;
  teamB: string;
  /** The baseline runner's snapshot, or null before the first tick. */
  live: {
    regime: PenelopeRegime;
    gate: string | null;
    stopReason: string | null;
    rests: { A: { px: number; size: number } | null; B: { px: number; size: number } | null };
    limitRejects?: number;
    lastError?: string | null;
  } | null;
}

function restsText(i: PenelopeStatusInput): string | null {
  const parts: string[] = [];
  const r = i.live?.rests;
  if (r?.A) parts.push(`${i.teamA} ${r.A.px}¢ ×${r.A.size}`);
  if (r?.B) parts.push(`${i.teamB} ${r.B.px}¢ ×${r.B.size}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Why a resting order is being held, from the gate text, in the trader's words. */
function holdReason(gate: string | null): string {
  if (!gate) return 'a thin book';
  if (/one-sided/.test(gate)) return 'a one-sided book';
  if (/stale/.test(gate)) return 'a quiet book';
  if (/spread/.test(gate)) return 'a wide spread';
  if (/depth/.test(gate)) return 'a depth dropout';
  return 'a thin book';
}

export function penelopeStatusSentence(i: PenelopeStatusInput): string {
  const suffix = i.practice ? ' (practice)' : '';
  if (!i.armed) return `stopped: not quoting${suffix}`;
  if (i.parked) return `not running: ${i.parked}`;
  const live = i.live;
  if (!live) return `starting…${suffix}`;
  const rests = restsText(i);
  let body: string;
  switch (live.regime) {
    case 'STOPPED':
      body = `stopped: ${live.stopReason ?? live.gate ?? 'game over'}`;
      break;
    case 'IDLE':
      body = `waiting: ${live.gate ?? 'game not started'}`;
      break;
    case 'PAUSED':
      body = `standing aside: ${live.gate ?? 'checking the book'}`;
      break;
    case 'HOLD':
      body = rests ? `holding ${rests} through ${holdReason(live.gate)}` : `nothing resting through ${holdReason(live.gate)}`;
      break;
    case 'PULL':
      body = rests ? `holding ${rests} through a Pinnacle pull` : 'Pinnacle pulled its line: nothing resting';
      break;
    case 'PAIR':
      body = rests ? `bracketing both sides while Pinnacle is off: ${rests}` : 'bracketing both sides while Pinnacle is off: nothing resting yet';
      break;
    case 'VALUE':
    default:
      body = rests ? `resting ${rests}` : 'watching for edge: nothing resting';
  }
  if ((live.limitRejects ?? 0) > 0 && live.lastError && /cap|limit/i.test(live.lastError)) {
    body += ` · your limit refused the last add (${live.lastError})`;
  }
  return body + suffix;
}
